import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { errMsg, money, sqft } from "@/lib/api";

// randomUUID is unavailable outside secure contexts (e.g. embeds on http:// pages).
const uid = () => Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");

export const inputCls = "w-full h-12 rounded-lg border px-3 text-[16px] bg-white brand-ring";

export function ContactFields({ f, setF, requirePhone, emailRequired }: any) {
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="space-y-3">
      <input className={inputCls} placeholder="Full name" aria-label="Full name" autoComplete="name" value={f.name} onChange={set("name")} required data-testid="contact-name-input" />
      <input className={inputCls} placeholder={emailRequired ? "Email" : "Email"} aria-label="Email" type="email" autoComplete="email" value={f.email} onChange={set("email")} required={emailRequired} data-testid="contact-email-input" />
      <input className={inputCls} placeholder={requirePhone ? "Phone" : "Phone (optional)"} aria-label="Phone" type="tel" autoComplete="tel" value={f.phone} onChange={set("phone")} required={requirePhone} data-testid="contact-phone-input" />
      {!emailRequired && <p className="text-[11px] text-slate-500">Email or phone is required.</p>}
      <input className="absolute -left-[9999px] h-0 w-0" tabIndex={-1} autoComplete="off" aria-hidden="true" value={f.company_website} onChange={set("company_website")} />
      <label className="flex gap-3 items-start text-xs text-slate-700">
        <input type="checkbox" className="mt-0.5 h-5 w-5 shrink-0" checked={f.consent_contact} onChange={(e) => setF({ ...f, consent_contact: e.target.checked })} data-testid="contact-consent-checkbox" />
        <span>I agree that {f.business || "this business"} may use this information to prepare my estimate and contact me about this request by email or phone. No booking or payment is made.</span>
      </label>
      {emailRequired && (
        <label className="flex gap-3 items-start text-xs text-slate-700">
          <input type="checkbox" className="mt-0.5 h-5 w-5 shrink-0" checked={!!f.marketing_opt_in} onChange={(e) => setF({ ...f, marketing_opt_in: e.target.checked })} data-testid="contact-marketing-checkbox" />
          <span>Optional: send me occasional offers and seasonal reminders by email. You can unsubscribe any time.</span>
        </label>
      )}
    </div>
  );
}

export const emptyContact = { name: "", email: "", phone: "", consent_contact: false, marketing_opt_in: false, company_website: "" };

export function ContactStep({ cfg, onBack, onSubmit, draft, onDraft }: any) {
  const [f, setF] = useState<any>(() => ({ ...emptyContact, business: cfg.business_name, ...(draft?.f || {}) }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const key = useRef(draft?.key || uid());
  useEffect(() => { onDraft?.({ f, key: key.current }); }, [f]); // eslint-disable-line react-hooks/exhaustive-deps
  const ok = f.name.trim() && /.+@.+\..+/.test(f.email) && (!cfg.require_phone || f.phone.trim()) && f.consent_contact;
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setError("");
    const { business, ...body } = f;
    try { await onSubmit({ ...body, phone: body.phone || null, idempotency_key: key.current }); } catch (err) { setError(errMsg(err)); setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4 fade-up" data-testid="contact-step">
      <div>
        <h2 className="font-heading text-xl font-bold">Your contact details</h2>
        <p className="text-sm text-slate-600 mt-1" data-testid="contact-explainer">{cfg.business_name} uses these details to prepare your estimate and follow up with you about it. Next, you’ll measure your property on a satellite map.</p>
      </div>
      <ContactFields f={f} setF={setF} requirePhone={cfg.require_phone} emailRequired />
      {error && <p className="text-sm text-red-700" role="alert" data-testid="contact-submit-error">{error}</p>}
      <div className="flex gap-2 pb-safe">
        <button type="button" className="h-12 w-12 rounded-lg border bg-white grid place-items-center" onClick={onBack} aria-label="Back" data-testid="contact-back-button"><ArrowLeft className="h-4 w-4" /></button>
        <button className="h-12 flex-1 rounded-lg brand-btn font-semibold" disabled={busy || !ok} data-testid="contact-submit-button">{busy ? "Saving…" : "Continue to measuring"}</button>
      </div>
    </form>
  );
}

export function EstimateStep({ est, onEdit, onRequest }: any) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const line = est.unit === "linear_ft";
  const request = async () => { setBusy(true); setError(""); try { await onRequest(); } catch (e) { setError(errMsg(e)); setBusy(false); } };
  return (
    <div className="space-y-5 fade-up" data-testid="estimate-step">
      <div className="rounded-xl border p-5 bg-slate-50">
        <p className="text-xs uppercase tracking-wider text-slate-500">Preliminary estimate · {est.service_label}</p>
        <p className="font-heading text-4xl font-extrabold mt-1 brand-text" data-testid="estimate-price">
          {est.show_range ? `${money(est.low_cents)} – ${money(est.high_cents)}` : money(est.estimate_cents)}
        </p>
        <div className="mt-3 text-sm text-slate-700 space-y-0.5">
          <p data-testid="estimate-area">Measured {line ? "length" : "area"}: <b className="font-mono">{Math.round(Number(est.quantity)).toLocaleString("en-US")} {line ? "linear ft" : "sq ft"}</b> (approximate)</p>
          {!line && Number(est.exclude_sq_ft) > 0 && <p className="text-xs text-slate-500">{sqft(est.include_sq_ft)} measured − {sqft(est.exclude_sq_ft)} excluded</p>}
          {est.minimum_applied && <p className="text-xs" data-testid="estimate-minimum-note">Includes the business’s minimum job charge.</p>}
        </div>
        <p className="mt-3 text-[11px] leading-snug text-slate-600" data-testid="estimate-disclaimer">{est.disclaimer}</p>
      </div>
      {error && <p className="text-sm text-red-700" role="alert" data-testid="request-service-error">{error}</p>}
      <div className="flex gap-2 pb-safe">
        <button type="button" className="h-12 px-4 rounded-lg border bg-white inline-flex items-center gap-1 text-sm" onClick={onEdit} data-testid="estimate-edit-button"><ArrowLeft className="h-4 w-4" />Edit</button>
        <button className="h-12 flex-1 rounded-lg brand-btn font-semibold" onClick={request} disabled={busy} data-testid="request-service-button">{busy ? "Sending…" : "Request Service"}</button>
      </div>
    </div>
  );
}

export function ManualForm({ cfg, service, address, reason, onSubmit, onBack }: any) {
  const [f, setF] = useState<any>({ ...emptyContact, address_text: address?.formatted || "", notes: "", service_id: service?.id || "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const key = useRef(uid());
  const msg: Record<string, string> = {
    maps_not_configured: "Online measuring isn’t available for this business yet.",
    maps_unavailable: "The satellite map couldn’t load.",
    map_quota_reached: "Online measuring is temporarily unavailable.",
    out_of_area: "This address may be outside the regular service area.",
    too_large: "This area is too large to price online.",
  };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setError("");
    try { await onSubmit({ ...f, service_id: f.service_id || null, reason, idempotency_key: key.current }); } catch (err) { setError(errMsg(err)); setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="manual-quote-form">
      <div>
        <h2 className="font-heading text-xl font-bold">Request a manual quote</h2>
        <p className="text-sm text-slate-600 mt-1">{reason && msg[reason] ? `${msg[reason]} ` : ""}{cfg.business_name} will review your property and contact you with a quote. No price is shown until they do.</p>
      </div>
      <select className={inputCls} value={f.service_id} onChange={(e) => setF({ ...f, service_id: e.target.value })} data-testid="manual-service-select">
        <option value="">Select a service (optional)</option>
        {cfg.services.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      <input className={inputCls} placeholder="Service address" value={f.address_text} onChange={(e) => setF({ ...f, address_text: e.target.value })} required data-testid="manual-address-input" />
      <textarea className={`${inputCls} h-24 py-2`} placeholder="Anything we should know? (optional)" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="manual-notes-input" />
      <ContactFields f={f} setF={setF} />
      {error && <p className="text-sm text-red-700" role="alert" data-testid="manual-submit-error">{error}</p>}
      <div className="flex gap-2 pb-safe">
        {onBack && <button type="button" className="h-12 w-12 rounded-lg border bg-white grid place-items-center" onClick={onBack} aria-label="Back" data-testid="manual-back-button"><ArrowLeft className="h-4 w-4" /></button>}
        <button className="h-12 flex-1 rounded-lg brand-btn font-semibold" disabled={busy || !f.name || f.address_text.length < 3 || (!f.email && !f.phone) || !f.consent_contact} data-testid="manual-submit-button">
          {busy ? "Sending…" : "Request My Quote"}
        </button>
      </div>
    </form>
  );
}
